import React, { useState, useEffect, useCallback } from 'react';
import { linkifyText } from '../../utils/linkUtils';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  Fade,
  useTheme,
  Paper,
  Grid,
  InputAdornment,
  IconButton,
  Stack,
  Chip,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FiberManualRecord as StatusIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useHeartbeat } from '../../contexts/HeartbeatContext';
import SuccessDialog from '../../components/SuccessDialog';
import api from '../../utils/api';
import './admin-css/AdminManagement-light.css';
import './admin-css/AdminManagement-dark.css';

interface Admin {
  _id: string;
  name: string;
  username: string;
  email: string;
  createdAt: string;
  isOnline: boolean;
  lastLogin?: string;
  lastHeartbeat?: string;
  themePreference?: string;
}

interface NewAdminFormValues {
  name: string;
  username: string;
  email: string;
  password: string;
}

const validationSchema = yup.object({
  name: yup.string().required('Full name is required'),
  username: yup
    .string()
    .matches(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores')
    .required('Username is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup
    .string()
    .min(8, 'Password should be of minimum 8 characters length')
    .required('Password is required'),
});

const editValidationSchema = yup.object({
  name: yup.string().required('Full name is required'),
  username: yup
    .string()
    .matches(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores')
    .required('Username is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
});

const AdminManagement: React.FC = () => {
  const theme = useTheme();
  const { connectionStatus, lastHeartbeat } = useHeartbeat();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [showOnlineMessage, setShowOnlineMessage] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 5000,
  });
  const [nameInputRef, setNameInputRef] = useState<HTMLInputElement | null>(null);

  const fetchAdmins = useCallback(async () => {
    try {
  
      const response = await api.get('/api/admin/admins');
      
      
      setAdmins(response.data);
    } catch (error: any) {
      console.error('❌ Admin fetch failed:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setSuccessDialogConfig({
        title: 'Error Loading Admins',
        message: error.response?.data?.message || 'Failed to fetch admins',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  // Check for URL parameter to open add admin dialog
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('openAddAdminDialog') === 'true') {
      setCreateDialogOpen(true);
      // Clean up the URL parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Auto-refresh admins every 15 seconds to match heartbeat interval
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchAdmins();
    }, 15000);

    return () => clearInterval(intervalId);
  }, [fetchAdmins]);

  // Refresh when heartbeat updates
  useEffect(() => {
    if (lastHeartbeat) {
      fetchAdmins();
    }
  }, [lastHeartbeat, fetchAdmins]);

  // Show online message when connection is established
  useEffect(() => {
    if (connectionStatus === 'connected' && admins.length > 0) {
      setShowOnlineMessage(true);
      const timer = setTimeout(() => setShowOnlineMessage(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [connectionStatus, admins.length]);

  // Focus first input when dialog opens
  useEffect(() => {
    if (createDialogOpen && nameInputRef) {
      const timer = setTimeout(() => {
        nameInputRef.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [createDialogOpen, nameInputRef]);

  const formik = useFormik<NewAdminFormValues>({
    initialValues: {
      name: '',
      username: '',
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await api.post('/api/admin/admins', values);
        resetForm();
        setCreateDialogOpen(false);
        fetchAdmins();
        setSuccessDialogConfig({
          title: 'Admin Created Successfully',
          message: 'The admin account has been created successfully.',
          autoCloseDelay: 5000,
        });
        setShowSuccessDialog(true);
      } catch (error: any) {
        setSuccessDialogConfig({
          title: 'Error Creating Admin',
          message: error.response?.data?.message || 'Failed to create admin',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      }
    },
  });

  const editFormik = useFormik({
    initialValues: {
      name: selectedAdmin?.name || '',
      username: selectedAdmin?.username || '',
      email: selectedAdmin?.email || '',
    },
    enableReinitialize: true,
    validationSchema: editValidationSchema,
    onSubmit: async (values) => {
      if (!selectedAdmin) return;

      try {
        await api.put(`/api/admin/admins/${selectedAdmin._id}`, values);
        setEditDialogOpen(false);
        setSelectedAdmin(null);
        fetchAdmins();
        setSuccessDialogConfig({
          title: 'Admin Updated Successfully',
          message: 'The admin account has been updated successfully.',
          autoCloseDelay: 5000,
        });
        setShowSuccessDialog(true);
      } catch (error: any) {
        setSuccessDialogConfig({
          title: 'Error Updating Admin',
          message: error.response?.data?.message || 'Failed to update admin',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      }
    },
  });

  const handleDeleteAdmin = async () => {
    if (!selectedAdmin) return;

    try {
      await api.delete(`/api/admin/admins/${selectedAdmin._id}`);
      setDeleteDialogOpen(false);
      setSelectedAdmin(null);
      fetchAdmins();
      setSuccessDialogConfig({
        title: 'Admin Deleted Successfully',
        message: 'The admin account has been deleted successfully.',
        autoCloseDelay: 5000,
      });
      setShowSuccessDialog(true);
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Deleting Admin',
        message: error.response?.data?.message || 'Failed to delete admin',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const getAdminOnlineStatus = (admin: Admin) => {
    // Simple online/offline status using backend-provided isOnline field
    return {
      isOnline: admin.isOnline,
      statusText: admin.isOnline ? 'Online' : 'Offline'
    };
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        <Typography>Loading admins...</Typography>
      </Box>
    );
  }

  return (
    <Box 
      className={`admin-management-container ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
      sx={{ p: 3 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h2" className="admin-title">
          Admin Management
        </Typography>
        <Button
          variant="contained"
          size={isMobile ? "small" : "medium"}
          onClick={() => setCreateDialogOpen(true)}
          className="add-admin-button"
          sx={{
            fontWeight: 600,
            borderRadius: isMobile ? '16px' : '999px',
            textTransform: 'none',
            px: isMobile ? 3 : 2,
            py: isMobile ? 0.75 : 1.5,
            backgroundColor: isMobile ? `${theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1B4672'}` : 'inherit',
            color: isMobile ? '#FFFFFF' : 'inherit',
            fontSize: isMobile ? '0.875rem' : 'inherit',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          }}
        >
          Add Admin
        </Button>
      </Box>

      <Fade in={showOnlineMessage}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Real-time connection established. Admin statuses will update automatically.
        </Alert>
      </Fade>

       <Grid container spacing={2}>
        {admins.map((admin) => {
          // Use the backend-provided isOnline status directly
          const isOnline = admin.isOnline;
          const statusInfo = getAdminOnlineStatus(admin);
          
          return (
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4} key={admin._id}>
               <Paper
                 className="admin-card-box"
                 sx={{
                   borderRadius: '12px',
                   border: `1px solid ${theme.palette.divider}`,
                   pt: '6%',
                   pb: '8%',
                   px: '6%',
                   backgroundColor: theme.palette.background.paper,
                   height: '100%',
                   minHeight: '320px', // Increased from 260px to accommodate longer content
                   display: 'flex',
                   flexDirection: 'column',
                   transition: 'all 0.3s ease-in-out',
                   '&:hover': {
                     transform: 'translateY(-4px)',
                   },
                 }}
               >
                {/* Top Row - Name + Status */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5, px: 0 }}>
                   <Typography variant="h4" sx={{ 
                     fontWeight: 800, 
                     color: theme.palette.text.primary,
                     wordBreak: 'break-word',
                     lineHeight: 1.2,
                     flex: 1,
                     mr: 1
                   }}>
                     <span dangerouslySetInnerHTML={{
                       __html: admin.name.split(' ').length === 2 
                         ? admin.name.replace(' ', '<br>') 
                         : admin.name
                     }} />
                   </Typography>
                  
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      color: isOnline ? theme.palette.success.main : theme.palette.error.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      marginLeft: 'auto',
                      marginRight: -1,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: isOnline ? theme.palette.success.main : theme.palette.error.main,
                        display: 'inline-block',
                      }}
                    />
                    {isOnline ? 'Online' : 'Offline'}
                  </Typography>
                </Stack>

                 {/* Middle Info */}
                 <Box sx={{ flex: 1, mb: 1.5 }}>
                   {/* Username */}
                   <Box sx={{ mb: 1.5 }}>
                     <Typography variant="body1" sx={{ 
                       color: theme.palette.text.primary, 
                       fontWeight: 400,
                       wordBreak: 'break-word',
                       lineHeight: 1.4
                     }}>
                       Username: <span className="admin-username">{admin.username}</span>
                     </Typography>
                   </Box>

                   {/* Email */}
                   <Box>
                     <Typography variant="body1" sx={{ 
                       color: theme.palette.text.primary, 
                       fontWeight: 400,
                       wordBreak: 'break-word',
                       lineHeight: 1.4,
                       overflowWrap: 'break-word'
                     }}>
                       Email: {linkifyText(admin.email, { color: 'text.primary' })}
                     </Typography>
                   </Box>
                 </Box>

                {/* Action Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  justifyContent: 'space-between', 
                  gap: 1, 
                  mt: 'auto',
                  width: '100%'
                }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setSelectedAdmin(admin);
                      setEditDialogOpen(true);
                    }}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                      fontSize: '0.75rem',
                      flex: 1,
                      minHeight: '32px',
                      backgroundColor: theme.palette.mode === 'dark' ? '#FFFFFF' : '#1B4672',
                      color: theme.palette.mode === 'dark' ? '#1B4672' : '#FFFFFF',
                      border: 'none',
                      boxShadow: theme.palette.mode === 'dark' 
                        ? '0 2px 4px rgba(0,0,0,0.3)' 
                        : '0 2px 4px rgba(0,0,0,0.1)',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#0f3a5f',
                        color: theme.palette.mode === 'dark' ? '#0f3a5f' : '#FFFFFF',
                        boxShadow: theme.palette.mode === 'dark' 
                          ? '0 4px 8px rgba(0,0,0,0.4)' 
                          : '0 4px 8px rgba(0,0,0,0.2)',
                      },
                    }}
                  >
                    Edit Admin
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setSelectedAdmin(admin);
                      setDeleteDialogOpen(true);
                    }}
                    disabled={isOnline}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                      fontSize: '0.75rem',
                      flex: 1,
                      minHeight: '32px',
                      backgroundColor: theme.palette.mode === 'dark' ? '#FFFFFF' : '#f44336',
                      color: theme.palette.mode === 'dark' ? '#1B4672' : '#FFFFFF',
                      border: 'none',
                      boxShadow: theme.palette.mode === 'dark' 
                        ? '0 2px 4px rgba(0,0,0,0.3)' 
                        : '0 2px 4px rgba(0,0,0,0.1)',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#d32f2f',
                        color: theme.palette.mode === 'dark' ? '#0f3a5f' : '#FFFFFF',
                        boxShadow: theme.palette.mode === 'dark' 
                          ? '0 4px 8px rgba(0,0,0,0.4)' 
                          : '0 4px 8px rgba(0,0,0,0.2)',
                      },
                      '&:disabled': {
                        backgroundColor: theme.palette.mode === 'dark' ? '#f0f0f0' : '#e0e0e0',
                        color: theme.palette.mode === 'dark' ? '#cccccc' : '#9e9e9e',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Delete Admin
                  </Button>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Create Admin Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0,0,0,0.3)' 
              : '0 8px 32px rgba(0,0,0,0.12)',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          {/* Header */}
          <Box sx={{ p: 3, pb: 2, textAlign: 'center' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1
              }}
            >
              Create Admin
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontSize: '0.9rem'
              }}
            >
              Fill out the form below to add a new administrator to the system.
            </Typography>
          </Box>

          {/* Form Content */}
          <DialogContent sx={{ px: 3, py: 1 }}>
            <Stack spacing={2.5}>
              {/* Full Name */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Full Name
                </Typography>
                <TextField 
                  fullWidth 
                  id="name" 
                  name="name" 
                  placeholder="Wissam Baltaji"
                  value={formik.values.name} 
                  onChange={formik.handleChange} 
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)} 
                  helperText={formik.touched.name && formik.errors.name}
                  variant="outlined"
                  inputRef={setNameInputRef}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                      color: theme.palette.text.primary,
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        transition: 'background-color 5000s ease-in-out 0s',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `2px solid ${theme.palette.primary.main}`,
                        borderRadius: '30px',
                      },
                    },
                  }}
                />
              </Box>

              {/* Username */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Username
                </Typography>
                <TextField 
                  fullWidth 
                  id="username" 
                  name="username" 
                  placeholder="Wissam"
                  value={formik.values.username} 
                  onChange={formik.handleChange} 
                  onBlur={formik.handleBlur}
                  error={formik.touched.username && Boolean(formik.errors.username)} 
                  helperText={formik.touched.username && formik.errors.username}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                      color: theme.palette.text.primary,
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        transition: 'background-color 5000s ease-in-out 0s',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `2px solid ${theme.palette.primary.main}`,
                        borderRadius: '30px',
                      },
                    },
                  }}
                />
              </Box>

              {/* Email Address */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Email Address
                </Typography>
                <TextField 
                  fullWidth 
                  id="email" 
                  name="email" 
                  type="email" 
                  placeholder="Wissam@theimpression.me"
                  value={formik.values.email} 
                  onChange={formik.handleChange} 
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)} 
                  helperText={formik.touched.email && formik.errors.email}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                      color: theme.palette.text.primary,
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        transition: 'background-color 5000s ease-in-out 0s',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `2px solid ${theme.palette.primary.main}`,
                        borderRadius: '30px',
                      },
                    },
                  }}
                />
              </Box>

              {/* Password */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Password
                </Typography>
                <TextField 
                  fullWidth 
                  id="password" 
                  name="password" 
                  type={showPassword ? 'text' : 'password'} 
                  value={formik.values.password} 
                  onChange={formik.handleChange} 
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)} 
                  helperText={formik.touched.password && formik.errors.password}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ 
                            color: theme.palette.text.secondary,
                            mr: 1
                          }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                      color: theme.palette.text.primary,
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        transition: 'background-color 5000s ease-in-out 0s',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `2px solid ${theme.palette.primary.main}`,
                        borderRadius: '30px',
                      },
                    },
                  }}
                />
              </Box>
            </Stack>
          </DialogContent>

          {/* Actions */}
          <Box sx={{ p: 3, pt: 2 }}>
            <Button 
              type="submit" 
              variant="contained"
              fullWidth
              disabled={formik.isSubmitting}
              className={`admin-primary-button ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{ 
                py: 1.5,
                '&:disabled': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#f0f0f0' : '#cccccc',
                  color: theme.palette.mode === 'dark' ? '#cccccc' : '#666666',
                }
              }}
            >
              {formik.isSubmitting ? 'Creating...' : 'Create Admin'}
            </Button>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button 
                onClick={() => setCreateDialogOpen(false)}
                className={`admin-secondary-button ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                sx={{ 
                  textTransform: 'none',
                  px: 3,
                  py: 1
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0,0,0,0.3)' 
              : '0 8px 32px rgba(0,0,0,0.12)',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }
        }}
      >
        <form onSubmit={editFormik.handleSubmit}>
          {/* Header */}
          <Box sx={{ p: 3, pb: 2, textAlign: 'center' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1
              }}
            >
              Edit Admin
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontSize: '0.9rem'
              }}
            >
              Update the administrator's information below.
            </Typography>
          </Box>

          {/* Form Content */}
          <DialogContent sx={{ px: 3, py: 1 }}>
            <Stack spacing={2.5}>
              {/* Full Name */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Full Name
                </Typography>
                <TextField
                  fullWidth
                  name="name"
                  placeholder="Enter full name"
                  value={editFormik.values.name}
                  onChange={editFormik.handleChange}
                  onBlur={editFormik.handleBlur}
                  error={editFormik.touched.name && Boolean(editFormik.errors.name)}
                  helperText={editFormik.touched.name && editFormik.errors.name}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                      color: theme.palette.text.primary,
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        transition: 'background-color 5000s ease-in-out 0s',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `2px solid ${theme.palette.primary.main}`,
                        borderRadius: '30px',
                      },
                    },
                  }}
                />
              </Box>

              {/* Username */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Username
                </Typography>
                <TextField
                  fullWidth
                  name="username"
                  placeholder="Enter username"
                  value={editFormik.values.username}
                  onChange={editFormik.handleChange}
                  onBlur={editFormik.handleBlur}
                  error={editFormik.touched.username && Boolean(editFormik.errors.username)}
                  helperText={editFormik.touched.username && editFormik.errors.username}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                      color: theme.palette.text.primary,
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        transition: 'background-color 5000s ease-in-out 0s',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `2px solid ${theme.palette.primary.main}`,
                        borderRadius: '30px',
                      },
                    },
                  }}
                />
              </Box>

              {/* Email */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  placeholder="Enter email address"
                  value={editFormik.values.email}
                  onChange={editFormik.handleChange}
                  onBlur={editFormik.handleBlur}
                  error={editFormik.touched.email && Boolean(editFormik.errors.email)}
                  helperText={editFormik.touched.email && editFormik.errors.email}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                      color: theme.palette.text.primary,
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        transition: 'background-color 5000s ease-in-out 0s',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '30px',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'} inset`,
                        WebkitTextFillColor: `${theme.palette.text.primary}`,
                        backgroundColor: `${theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)'}`,
                        border: `2px solid ${theme.palette.primary.main}`,
                        borderRadius: '30px',
                      },
                    },
                  }}
                />
              </Box>
            </Stack>
          </DialogContent>

          {/* Actions */}
          <Box sx={{ p: 3, pt: 2 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                onClick={() => setEditDialogOpen(false)}
                className={`admin-secondary-button ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                sx={{
                  textTransform: 'none',
                  px: 3,
                  py: 1
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={editFormik.isSubmitting}
                className={`admin-primary-button ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                sx={{
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  '&:disabled': {
                    backgroundColor: theme.palette.mode === 'dark' ? '#f0f0f0' : '#cccccc',
                    color: theme.palette.mode === 'dark' ? '#cccccc' : '#666666',
                  }
                }}
              >
                {editFormik.isSubmitting ? 'Updating...' : 'Update Admin'}
              </Button>
            </Stack>
          </Box>
        </form>
      </Dialog>

      {/* Delete Admin Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.08)',
            maxWidth: '320px',
            width: '90%'
          }
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Delete Admin
          </Typography>
          
          <Typography variant="body1" sx={{ fontWeight: 400, mb: 3, maxWidth: '240px', mx: 'auto' }}>
            Are you sure you want to delete<br />
            the admin <strong>"{selectedAdmin?.name}"</strong>?
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, alignItems: 'center' }}>
            <Button 
              onClick={handleDeleteAdmin} 
              variant="contained"
              className={`admin-primary-button ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{ 
                py: 1.5,
                width: '80%'
              }}
            >
              Delete
            </Button>
            
            <Button 
              onClick={() => setDeleteDialogOpen(false)}
              variant="outlined"
              className={`admin-secondary-button ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{ 
                py: 1.5,
                width: '80%'
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={successDialogConfig.title}
        message={successDialogConfig.message}
        buttonText="Got it"
        autoCloseDelay={5000}
      />
    </Box>
  );
};

export default AdminManagement;                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                   